import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import posthog from 'posthog-js';
import { Download, Plus } from 'lucide-react';
import AdminTabs from '../AdminTabs';
import {
  AdminHead,
  AdminPage,
  BulkBar,
  Button,
  ConfirmDialog,
  FilterMenu,
  SearchField,
  Segmented,
} from '../kit';
import { useUserSettingsContext } from '../../../context/UserSettingsContext';
import { useIsMobile } from '../../../hooks/use-mobile';
import { startEmulation } from '../../../utils/emulation';
import { downloadUserExport, userExportFileName, userExportRows } from '../userExport';
import UsersTable from './UsersTable';
import UsersCardList from './UsersCardList';
import UserActionSheet from './UserActionSheet';
import UserPanel from './UserPanel';
import UsageView from './usage/UsageView';
import { AddToOrgDialog, BulkResultDialog, PasswordResetDialog, SecretDialog, SetReportAccessDialog } from './Dialogs';
import { useDebounced, useDirectory, useUsersList } from './hooks';
import {
  apiErrorMessage,
  bulkUsers,
  deleteUserPermanently,
  listUsers,
  resendInvite,
  setUserDeactivated,
} from './usersApi';
import {
  PAGE_SIZES,
  REPORT_GROUPS,
  ROLE_FILTER_OPTIONS,
  STATUS_OPTIONS,
  displayName,
  filtersFromSearchParams,
  filtersToQuery,
  filtersToSearchParams,
  hasActiveFilters,
  isDeactivated,
  isLodgeicStaffEmail,
  isPlatformAdmin,
  orgRoleChips,
  showingLabel,
  userStatus,
} from './userModel';
import './users.css';

const BULK_LABELS = {
  addToOrg: 'Added to organization',
  setReportAccess: 'Report access set',
  resendInvite: 'Invites re-sent',
  deactivate: 'Deactivated',
};

export default function UsersPage() {
  const { userSettings } = useUserSettingsContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = useMemo(() => filtersFromSearchParams(searchParams), [searchParams]);
  const directory = useDirectory(userSettings);
  const list = useUsersList(filters);
  const viewerIsLodgeicAdmin = isPlatformAdmin(userSettings) && isLodgeicStaffEmail(userSettings?.email);

  const [searchText, setSearchText] = useState(filters.q);
  const debouncedSearch = useDebounced(searchText, 300);
  const [selected, setSelected] = useState(() => new Map()); // id → user
  const [panel, setPanel] = useState(null); // { key, id, initial, presetOrg }
  const [dialog, setDialog] = useState(null); // { type, user? }
  const [busy, setBusy] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [exporting, setExporting] = useState(false);
  const isMobile = useIsMobile();
  const [sheetUser, setSheetUser] = useState(null); // phones: the row whose action sheet is open

  const view = searchParams.get('view') === 'usage' ? 'usage' : 'directory';
  const setView = (next) => setSearchParams(next === 'usage' ? { view: 'usage' } : {}, { replace: false });

  const setFilters = useCallback((patch, { resetPage = true } = {}) => {
    setSearchParams((prev) => {
      const current = filtersFromSearchParams(prev);
      const next = { ...current, ...patch };
      if (resetPage && !('page' in patch)) next.page = 1;
      const out = filtersToSearchParams(next);
      if (prev.get('view')) out.view = prev.get('view');
      return out;
    }, { replace: true });
  }, [setSearchParams]);

  // Search box → URL (debounced); URL → search box (back/forward, links).
  useEffect(() => {
    if (debouncedSearch !== filters.q) setFilters({ q: debouncedSearch });
  }, [debouncedSearch]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    setSearchText((cur) => (cur === filters.q ? cur : filters.q));
  }, [filters.q]);

  // ?org=<numeric id> (Organizations page links) → org code once known.
  useEffect(() => {
    if (!filters.orgs.some((t) => /^\d+$/.test(t))) return;
    const mapped = filters.orgs.map((t) => (/^\d+$/.test(t) && directory.byId.get(t) ? directory.byId.get(t).code : t));
    if (mapped.join(',') !== filters.orgs.join(',')) setFilters({ orgs: mapped }, { resetPage: false });
  }, [filters.orgs, directory.byId, setFilters]);

  // Selection is per result set: clear it when the filters or page change.
  const filterKey = JSON.stringify(filtersToQuery(filters));
  useEffect(() => { setSelected(new Map()); }, [filterKey]);

  const totalPages = Math.max(1, Math.ceil(list.total / filters.size));
  useEffect(() => {
    if (list.loaded && !list.loading && filters.page > totalPages) setFilters({ page: totalPages }, { resetPage: false });
  }, [list.loaded, list.loading, filters.page, totalPages, setFilters]);

  // ── Actions ─────────────────────────────────────────────────────────────
  const openUser = (user) => setPanel({ key: `u:${user.id}:${Date.now()}`, id: user.id, initial: user });
  const openInvite = () => setPanel({
    key: `new:${Date.now()}`,
    id: null,
    presetOrg: filters.orgs.length === 1 && directory.manageable.has(filters.orgs[0]) ? filters.orgs[0] : null,
  });

  const emulate = (user) => {
    startEmulation(user, userSettings);
    posthog.identify(user.email, { email: user.email, name: user.firstName });
    posthog.setPersonPropertiesForFlags({ email: user.email, name: user.firstName });
    toast.success(`Now emulating ${displayName(user)}. Opening the dashboard…`, { duration: 3000 });
    setTimeout(() => { window.location.href = '/dashboard'; }, 500);
  };

  const withBusy = async (fn) => {
    setBusy(true);
    try { await fn(); } finally { setBusy(false); }
  };

  const deactivateOne = (user) => withBusy(async () => {
    try {
      const saved = await setUserDeactivated(user.id, true);
      toast.success(saved.cognitoDisabled === false ? 'Deactivated, but sign-in could not be disabled' : `${displayName(user)} was deactivated`);
      setDialog(null);
      list.reload();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Couldn't deactivate this user"));
    }
  });

  const restoreOne = (user) => withBusy(async () => {
    try {
      const saved = await setUserDeactivated(user.id, false);
      if (saved.cognitoEnabled && saved.welcomeEmailSent) toast.success('Restored — a new temporary password was emailed');
      else if (saved.cognitoEnabled) toast.error('Restored, but the email failed — use Reset password');
      else toast.error('Restored, but sign-in could not be re-enabled');
      setDialog(null);
      list.reload();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Couldn't restore this user"));
    }
  });

  const deleteOne = (user) => withBusy(async () => {
    try {
      const res = await deleteUserPermanently(user.id);
      if (res?.cognitoDeleted === false) toast.error(`Deleted here, but the sign-in account remains: ${res.cognitoDeleteMessage || 'unknown error'}`);
      else toast.success(`${user.email} was deleted`);
      setDialog(null);
      list.reload();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Couldn't delete this user"));
    }
  });

  const resendOne = async (user) => {
    try {
      const res = await resendInvite(user.id);
      if (res.status === 'sent') {
        toast.success(`Invite re-sent to ${user.email}`);
        list.reload();
      } else if (res.inviteUrl || res.temporaryPassword) {
        setDialog({ type: 'secret', user, message: res.message, secret: res.inviteUrl || res.temporaryPassword });
      } else {
        toast.error(res.message || "Couldn't resend the invite");
      }
    } catch (err) {
      toast.error(apiErrorMessage(err, "Couldn't resend the invite"));
    }
  };

  const isViewer = (u) => String(u?.email || '').toLowerCase() === String(userSettings?.email || '').toLowerCase();
  const rowActions = (u) => {
    const status = u.adminMeta?.status || userStatus(u);
    const off = isDeactivated(u);
    const self = isViewer(u);
    return [
      { key: 'edit', label: 'Edit', onSelect: () => openUser(u) },
      off ? null : { key: 'password', label: 'Reset password', onSelect: () => setDialog({ type: 'password', user: u }) },
      off || self ? null : { key: 'emulate', label: 'Emulate', onSelect: () => setDialog({ type: 'emulate', user: u }) },
      status === 'invited' ? { key: 'resend', label: 'Resend invite', onSelect: () => resendOne(u) } : null,
      self ? null : off
        ? { key: 'restore', label: 'Restore access', separatorBefore: true, onSelect: () => setDialog({ type: 'restore', user: u }) }
        : { key: 'deactivate', label: 'Deactivate', separatorBefore: true, onSelect: () => setDialog({ type: 'deactivate', user: u }) },
      self ? null : { key: 'delete', label: 'Delete permanently', danger: true, onSelect: () => setDialog({ type: 'delete', user: u }) },
    ];
  };

  // ── Selection + bulk ────────────────────────────────────────────────────
  const toggle = (u) => setSelected((m) => {
    const next = new Map(m);
    if (next.has(u.id)) next.delete(u.id); else next.set(u.id, u);
    return next;
  });
  const toggleAll = (on) => setSelected((m) => {
    const next = new Map(m);
    list.rows.forEach((u) => { if (on) next.set(u.id, u); else next.delete(u.id); });
    return next;
  });

  const runBulk = (action, params = {}) => withBusy(async () => {
    const ids = [...selected.keys()];
    try {
      const res = await bulkUsers(action, ids, params);
      setDialog(null);
      if (res.succeeded) toast.success(`${BULK_LABELS[action]}: ${res.succeeded} user${res.succeeded === 1 ? '' : 's'}`);
      if (res.failed || res.skipped) setBulkResult(res);
      else setSelected(new Map());
      list.reload();
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Bulk action failed'));
    }
  });

  // ── Export ──────────────────────────────────────────────────────────────
  const exportUsers = async (users, search) => {
    if (!users.length) { toast.error('No users to export'); return; }
    await downloadUserExport(userExportRows(users, directory.locations, directory.orgNameByCode), userExportFileName(search));
    toast.success(`Exported ${users.length} user${users.length === 1 ? '' : 's'}`);
  };
  const exportAll = async () => {
    setExporting(true);
    try {
      const res = await listUsers(filtersToQuery(filters, { all: true }));
      await exportUsers(res.users, filters.q);
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Export failed'));
    } finally {
      setExporting(false);
    }
  };

  // ── Panel callbacks ─────────────────────────────────────────────────────
  const onSaved = (saved, { isNew }) => {
    setPanel(null);
    if (isNew) { list.reload(); return; }
    list.patchRow(saved.id, (row) => {
      const merged = { ...row, ...saved };
      const orgs = orgRoleChips({ orgAccess: merged.orgAccess }, directory.orgNameByCode)
        .map((c) => ({ ...(row.adminMeta?.orgs || []).find((o) => o.code === c.code), code: c.code, name: c.name, role: c.role }));
      return { ...merged, adminMeta: { ...(row.adminMeta || {}), orgs, status: userStatus(merged) } };
    });
  };

  // ── Render ──────────────────────────────────────────────────────────────
  const counts = list.counts;
  const statusOptions = STATUS_OPTIONS.map((o) => ({ ...o, count: counts ? counts[o.value] : undefined }));
  const orgOptions = directory.orgs.map((o) => ({ value: o.code, label: o.name }));
  const filtered = hasActiveFilters(filters);
  const emptyState = filtered
    ? {
        title: 'No users match these filters',
        body: 'Try a different search, or clear the filters.',
        action: <Button onClick={() => { setSearchText(''); setSearchParams({}, { replace: true }); }}>Clear filters</Button>,
      }
    : {
        title: 'No users yet',
        body: 'Invite someone to give them access to an organization.',
        action: <Button variant="primary" icon={Plus} onClick={openInvite}>Invite user</Button>,
      };
  const selectedUsers = [...selected.values()];
  const viewTabs = [
    { key: 'directory', label: 'Directory', count: counts ? counts.all : undefined },
    { key: 'usage', label: 'Usage' },
  ];
  const d = dialog;
  const exportTitle = filtered ? 'Export every user matching the current filters' : 'Export every user';
  const filterControls = (
    <>
      <Segmented label="Status" options={statusOptions} value={filters.status} onChange={(status) => setFilters({ status })} />
      <FilterMenu label="Organization" searchable options={orgOptions} value={filters.orgs} onChange={(orgs) => setFilters({ orgs })} />
      <FilterMenu label="Role" options={ROLE_FILTER_OPTIONS} value={filters.roles} onChange={(roles) => setFilters({ roles })} />
      <FilterMenu
        label="Report access"
        options={REPORT_GROUPS.map((g) => ({ value: g.value, label: g.label, hint: g.code }))}
        value={filters.reports}
        onChange={(reports) => setFilters({ reports })}
      />
    </>
  );
  const prevPage = () => setFilters({ page: filters.page - 1 }, { resetPage: false });
  const nextPage = () => setFilters({ page: filters.page + 1 }, { resetPage: false });

  return (
    <AdminPage className="us-page">
      <AdminTabs />
      {view === 'usage' ? (
        <UsageView
          tabs={viewTabs}
          activeTab={view}
          onTabChange={setView}
          onOpenUser={(id) => setPanel({ key: `u:${id}:${Date.now()}`, id, initial: null })}
        />
      ) : (
      <>
      <AdminHead
        title="Users"
        subtitle="Who can sign in, which organizations they see, and what they can do there."
        tabs={viewTabs}
        activeTab={view}
        onTabChange={setView}
        actions={(
          <>
            {isMobile ? (
              <Button icon={Download} onClick={exportAll} disabled={exporting || !list.total}
                title={exportTitle} aria-label={exporting ? 'Exporting…' : 'Export'} />
            ) : (
              <Button icon={Download} onClick={exportAll} disabled={exporting || !list.total}
                title={exportTitle}>
                {exporting ? 'Exporting…' : 'Export'}
              </Button>
            )}
            <Button variant="primary" icon={Plus} onClick={openInvite}>Invite user</Button>
          </>
        )}
      />

      <div className="ak-frame">
        <div className={`ak-toolbar${isMobile ? ' ak-m-toolbar' : ''}`}>
          <SearchField value={searchText} onChange={setSearchText} placeholder={isMobile ? 'Search users' : 'Search name, email or organization'} label="Search users" />
          {isMobile ? <div className="ak-m-filters" role="group" aria-label="Filters">{filterControls}</div> : filterControls}
          <span className="ak-toolbar-meta" aria-live="polite">
            {list.loaded ? showingLabel(filters.page, filters.size, list.total) : 'Loading…'}
          </span>
        </div>

        {isMobile ? null : (
          <BulkBar count={selected.size} onClear={() => setSelected(new Map())}>
            <Button onClick={() => setDialog({ type: 'bulk-add' })} disabled={busy}>Add to organization</Button>
            <Button onClick={() => setDialog({ type: 'bulk-reports' })} disabled={busy}>Set report access</Button>
            <Button onClick={() => runBulk('resendInvite')} disabled={busy}>Resend invites</Button>
            <Button onClick={() => exportUsers(selectedUsers, 'selected')} disabled={busy}>Export selected</Button>
            <Button variant="danger" onClick={() => setDialog({ type: 'bulk-deactivate' })} disabled={busy}>Deactivate</Button>
          </BulkBar>
        )}

        {isMobile ? (
          <UsersCardList
            rows={list.rows}
            loading={list.loading}
            loaded={list.loaded}
            error={list.error ? apiErrorMessage(list.error, '') : null}
            onRetry={list.reload}
            onOpen={openUser}
            onActions={setSheetUser}
            orgNameByCode={directory.orgNameByCode}
            emptyState={emptyState}
          />
        ) : (
          <UsersTable
            rows={list.rows}
            loading={list.loading}
            loaded={list.loaded}
            error={list.error ? apiErrorMessage(list.error, '') : null}
            onRetry={list.reload}
            selected={selected}
            onToggle={toggle}
            onToggleAll={toggleAll}
            onOpen={openUser}
            rowActions={rowActions}
            orgNameByCode={directory.orgNameByCode}
            emptyState={emptyState}
          />
        )}

        {list.total > 0 && isMobile ? (
          <div className="ak-pager ak-m-pager">
            <Button onClick={prevPage} disabled={filters.page <= 1}>Previous</Button>
            <span>Page {filters.page} of {totalPages}</span>
            <Button onClick={nextPage} disabled={filters.page >= totalPages}>Next</Button>
          </div>
        ) : null}
        {list.total > 0 && !isMobile ? (
          <div className="ak-pager">
            <label className="us-pager-size">
              Rows per page
              <select value={filters.size} onChange={(e) => setFilters({ size: Number(e.target.value) })}>
                {PAGE_SIZES.map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </label>
            <span className="ak-foot-spacer" />
            <span>Page {filters.page} of {totalPages}</span>
            <Button size="small" onClick={prevPage} disabled={filters.page <= 1}>Previous</Button>
            <Button size="small" onClick={nextPage} disabled={filters.page >= totalPages}>Next</Button>
          </div>
        ) : null}
      </div>
      </>
      )}

      {panel ? (
        <UserPanel
          key={panel.key}
          userId={panel.id}
          initialUser={panel.initial}
          presetOrgCode={panel.presetOrg}
          directory={directory}
          viewerIsLodgeicAdmin={viewerIsLodgeicAdmin}
          viewerEmail={userSettings?.email}
          onClose={() => setPanel(null)}
          onSaved={onSaved}
          onChanged={list.reload}
          onDeleted={() => { setPanel(null); list.reload(); }}
          onOpenUser={(id) => setPanel({ key: `u:${id}:${Date.now()}`, id, initial: null })}
          onEmulate={emulate}
        />
      ) : null}

      {isMobile ? (
        <UserActionSheet
          user={sheetUser}
          items={sheetUser ? rowActions(sheetUser) : []}
          onClose={() => setSheetUser(null)}
        />
      ) : null}

      <PasswordResetDialog open={d?.type === 'password'} user={d?.user} onClose={() => setDialog(null)} />
      <SecretDialog open={d?.type === 'secret'} title="Invite email failed" message={d?.message} secret={d?.secret} onClose={() => setDialog(null)} />

      <ConfirmDialog open={d?.type === 'emulate'} title={`Emulate ${displayName(d?.user)}?`} confirmLabel="Start emulating"
        onCancel={() => setDialog(null)} onConfirm={() => { const u = d.user; setDialog(null); emulate(u); }}>
        <p>You'll browse the app as {d?.user?.email} until you stop emulating.</p>
      </ConfirmDialog>

      <ConfirmDialog open={d?.type === 'deactivate'} title={`Deactivate ${displayName(d?.user)}?`} confirmLabel="Deactivate" danger busy={busy}
        onCancel={() => setDialog(null)} onConfirm={() => deactivateOne(d.user)}>
        <p>They're signed out of Lodgeic and can't sign in. Their access and settings are kept, so you can restore them later.</p>
      </ConfirmDialog>

      <ConfirmDialog open={d?.type === 'restore'} title={`Restore ${displayName(d?.user)}?`} confirmLabel="Restore access" busy={busy}
        onCancel={() => setDialog(null)} onConfirm={() => restoreOne(d.user)}>
        <p>Re-enables their sign-in and emails them a new temporary password.</p>
      </ConfirmDialog>

      <ConfirmDialog open={d?.type === 'delete'} title="Delete permanently?" confirmLabel="Delete permanently" danger busy={busy}
        onCancel={() => setDialog(null)} onConfirm={() => deleteOne(d.user)}>
        <p>Removes {d?.user?.email}'s record, sign-in account, report subscriptions and alerts. This can't be undone — deactivate instead if they might come back.</p>
      </ConfirmDialog>

      <ConfirmDialog open={d?.type === 'bulk-deactivate'} title={`Deactivate ${selected.size} user${selected.size === 1 ? '' : 's'}?`}
        confirmLabel="Deactivate" danger busy={busy} onCancel={() => setDialog(null)} onConfirm={() => runBulk('deactivate')}>
        <p>They're signed out and can't sign in until restored. Already-deactivated users and your own account are skipped.</p>
      </ConfirmDialog>

      <AddToOrgDialog
        open={d?.type === 'bulk-add'}
        count={selected.size}
        orgs={directory.orgs}
        locationsForOrg={directory.locationsForOrg}
        busy={busy}
        onClose={() => setDialog(null)}
        onSubmit={(params) => runBulk('addToOrg', params)}
      />
      <SetReportAccessDialog
        open={d?.type === 'bulk-reports'}
        count={selected.size}
        orgs={directory.orgs}
        busy={busy}
        onClose={() => setDialog(null)}
        onSubmit={(params) => runBulk('setReportAccess', params)}
      />
      <BulkResultDialog result={bulkResult} onClose={() => setBulkResult(null)} />
    </AdminPage>
  );
}
